<template>
  <div class="promedios-forma">
    <h2 class="pf-titulo">Promedios por Forma</h2>
    <p class="pf-descripcion">
      Sube el Excel con las columnas de puntuación transformada (sufijo
      <code>t</code> para Forma A, <code>bt</code> para Forma B). Se calcula
      el promedio de cada variable por cada columna <code>AGR-*</code> del
      archivo y se colorea según el rango oficial de riesgo psicosocial.
    </p>

    <div
      class="pf-upload-zone"
      :class="{
        'pf-upload-zone--empty': status !== 'calculado',
        'pf-upload-zone--done': status === 'calculado',
      }"
    >
      <template v-if="status === 'calculado'">
        <div class="pf-upload-icon pf-upload-icon--done">
          <i class="material-icons">check_circle</i>
        </div>
        <div class="pf-upload-text">
          <strong>{{ file.name }}</strong>
          <span>{{ fileSummary }}</span>
        </div>
        <div class="pf-upload-actions">
          <button class="pf-btn pf-btn-ghost" v-on:click="selectFile">
            <i class="material-icons">refresh</i>
            Cambiar archivo
          </button>
          <button
            class="pf-btn pf-btn-primary"
            v-on:click="downloadExcel"
          >
            <i class="material-icons">download</i>
            Descargar Excel calculado
          </button>
        </div>
      </template>
      <template v-else>
        <div class="pf-upload-icon">
          <i class="material-icons">cloud_upload</i>
        </div>
        <div class="pf-upload-empty-text">
          <strong>Arrastra tu Excel aquí, o haz clic para buscarlo</strong>
          <span>
            Formato .xlsx — necesita al menos una columna de puntuación
            (sufijo <code>t</code> o <code>bt</code>) y una columna
            <code>AGR-*</code>
          </span>
        </div>
        <div class="pf-upload-empty-actions">
          <button class="pf-btn pf-btn-ghost" v-on:click="downloadTemplate">
            <i class="material-icons">description</i>
            Descargar plantilla
          </button>
          <button class="pf-btn pf-btn-primary" v-on:click="selectFile">
            <i class="material-icons">upload_file</i>
            Elegir archivo
          </button>
        </div>
      </template>

      <input
        ref="inputFile"
        class="pf-upload-input"
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        name="data"
        v-on:change="onFileChange"
      />
    </div>

    <p v-if="status === 'error'" class="pf-upload-error">{{ errorMessage }}</p>
    <p v-if="status === 'idle'" class="pf-table-note">
      Sin archivo todavía no hay nada que calcular — no se muestran leyenda de
      rangos, tabla ni botón de descarga hasta que se suba y valide un Excel.
    </p>

    <div v-if="status === 'calculado'" class="pf-resultado">
      <div class="pf-result-meta">
        <span>
          Combinaciones Grupo/SubGrupo:
          <strong>{{ result.combinations.length }}</strong>
        </span>
        <span>
          Variables calculadas: <strong>{{ totalVariables }}</strong>
        </span>
        <b-button
          v-if="excludedCombinations.length"
          type="is-warning"
          size="is-small"
          rounded
          icon-left="alert-circle-outline"
          v-on:click="showExcluded = true"
        >
          {{ excludedCombinations.length }} excluidas por datos
          insuficientes — clic para ver
        </b-button>
      </div>

      <b-modal
        :active.sync="showExcluded"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <div class="modal-card" style="width: 480px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Combinaciones excluidas ({{ excludedCombinations.length }})
            </p>
          </header>
          <section class="modal-card-body">
            <p class="pf-table-note" style="margin-top: 0;">
              Ninguna fila válida para calcular estas combinaciones
              Grupo/SubGrupo — no aparecen en el Excel descargado.
            </p>
            <ul class="pf-excluidas-list">
              <li v-for="name in excludedCombinations" :key="name">
                {{ name }}
              </li>
            </ul>
          </section>
          <footer class="modal-card-foot" style="justify-content: flex-end;">
            <button class="button" type="button" v-on:click="showExcluded = false">
              Cerrar
            </button>
          </footer>
        </div>
      </b-modal>

      <div class="pf-legend-row">
        <div class="pf-toolbar-left">
          <div class="pf-view-switch">
            <span
              class="pf-view-switch__opt"
              :class="{ 'pf-view-switch__opt--active': activeFormView === 'A' }"
              v-on:click="activeFormView = 'A'"
            >
              Forma A
            </span>
            <span
              class="pf-view-switch__opt"
              :class="{ 'pf-view-switch__opt--active': activeFormView === 'B' }"
              v-on:click="activeFormView = 'B'"
            >
              Forma B
            </span>
          </div>
          <b-input
            v-model="search"
            class="pf-search"
            icon-left="magnify"
            placeholder="Buscar por grupo o subgrupo..."
          />
        </div>
        <div class="pf-legend">
          <span
            v-for="level in levelLegend"
            :key="level.id"
            class="pf-legend-chip"
            :style="{
              backgroundColor: level.background,
              borderColor: level.border,
              color: level.text,
            }"
          >
            {{ level.label }}
          </span>
        </div>
      </div>

      <p v-if="search.trim() && !visibleCombinations.length" class="pf-table-note">
        Ningún grupo o subgrupo coincide con "{{ search }}".
      </p>

      <div class="pf-table-wrap">
        <table class="pf-result-table">
          <thead>
            <tr>
              <th class="pf-col-group">Grupo</th>
              <th class="pf-col-subgroup">SubGrupo</th>
              <th class="pf-col-n" v-tooltip.top="'Cantidad de colaboradores en esta combinación, para la Forma seleccionada'">N</th>
              <th v-for="column in visibleColumnCatalog" :key="column.code">
                {{ column.name }} Forma {{ column.form }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="combination in visibleCombinations"
              :key="combination.group + ' = ' + combination.subgroup"
            >
              <td class="pf-col-group">{{ combination.group }}</td>
              <td
                class="pf-col-subgroup"
                v-tooltip.right="`Grupo: ${combination.group}`"
              >
                {{ combination.subgroup }}
              </td>
              <td class="pf-col-n">{{ combination.formCounts[activeFormView] }}</td>
              <td
                v-for="column in visibleColumnCatalog"
                :key="column.code"
                class="pf-metric"
                :class="{
                  'pf-metric--blank':
                    cellValue(combination, column) === null,
                }"
                :style="cellStyle(combination, column)"
              >
                {{ cellText(combination, column) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="pf-table-note">
        Las celdas en blanco (—) son variables sin ningún dato válido para esa
        combinación Grupo/SubGrupo — no se muestran como 0,0.
      </p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx-js-style";
import {
  FORM_A_CATALOG,
  FORM_B_CATALOG,
  RANGO_FP_PALETTE,
  detectCatalogColumns,
  detectGroupingColumns,
  filterColumnsWithData,
  calculateAveragesByForm,
  classifyValue,
  buildWorkbook,
  round1,
  TRANSFORMED_SCORE_TEMPLATE_COLUMNS,
} from "./promedios-forma-helpers";
import { generateFrpTemplate } from "../../../helpers";

var FILES_ACCEPT = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export default {
  data() {
    return {
      file: { name: "", raw: null },
      status: "idle",
      errorMessage: "",
      result: null,
      excludedCombinations: [],
      showExcluded: false,
      fileRowCount: 0,
      search: "",
      activeFormView: "A",
    };
  },
  computed: {
    columnCatalog() {
      var activeCatalog =
        this.activeFormView === "B" ? FORM_B_CATALOG : FORM_A_CATALOG;

      return activeCatalog.map(function (entry) {
        return {
          code: entry.code,
          name: entry.name,
          form: this.activeFormView,
        };
      }, this);
    },
    totalVariables() {
      return FORM_A_CATALOG.length + FORM_B_CATALOG.length;
    },
    visibleColumnCatalog() {
      if (!this.result) {
        return [];
      }

      return filterColumnsWithData(
        this.columnCatalog,
        this.result.combinations
      );
    },
    fileSummary() {
      return (
        this.fileRowCount +
        " filas · " +
        FORM_A_CATALOG.length +
        " columnas Forma A · " +
        FORM_B_CATALOG.length +
        " columnas Forma B"
      );
    },
    levelLegend() {
      return [
        {
          id: "veryHigh",
          label: "Riesgo muy alto",
          background: RANGO_FP_PALETTE.veryHigh.background,
          border: RANGO_FP_PALETTE.veryHigh.border,
          text: RANGO_FP_PALETTE.veryHigh.text,
        },
        {
          id: "high",
          label: "Riesgo alto",
          background: RANGO_FP_PALETTE.high.background,
          border: RANGO_FP_PALETTE.high.border,
          text: RANGO_FP_PALETTE.high.text,
        },
        {
          id: "medium",
          label: "Riesgo medio",
          background: RANGO_FP_PALETTE.medium.background,
          border: RANGO_FP_PALETTE.medium.border,
          text: RANGO_FP_PALETTE.medium.text,
        },
        {
          id: "low",
          label: "Riesgo bajo",
          background: RANGO_FP_PALETTE.low.background,
          border: RANGO_FP_PALETTE.low.border,
          text: RANGO_FP_PALETTE.low.text,
        },
        {
          id: "noRisk",
          label: "Sin riesgo o riesgo despreciable",
          background: RANGO_FP_PALETTE.noRisk.background,
          border: RANGO_FP_PALETTE.noRisk.border,
          text: RANGO_FP_PALETTE.noRisk.text,
        },
      ];
    },
    visibleCombinations() {
      if (!this.result) {
        return [];
      }

      var list = this.result.combinations.slice().sort(function (a, b) {
        if (a.group !== b.group) {
          return a.group.localeCompare(b.group);
        }
        return a.subgroup.localeCompare(b.subgroup);
      });

      var term = this.search.trim().toLowerCase();

      if (!term) {
        return list;
      }

      return list.filter(function (combination) {
        return (
          combination.group.toLowerCase().indexOf(term) !== -1 ||
          combination.subgroup.toLowerCase().indexOf(term) !== -1
        );
      });
    },
  },
  methods: {
    selectFile() {
      this.$refs.inputFile.click();
    },
    onFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;

      if (!files.length) {
        return;
      }

      if (FILES_ACCEPT.indexOf(files[0].type) === -1) {
        this.showError(
          "El archivo solo puede ser tipo Excel (.xlsx/.xls)."
        );
        return;
      }

      var selectedFile = files[0];
      var reader = new FileReader();
      var self = this;

      reader.onload = function (event) {
        var wb = XLSX.read(event.target.result, { type: "array" });
        var ws = wb.Sheets[wb.SheetNames[0]];
        var rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        var headers = rows[0] || [];
        var dataRows = rows.slice(1);

        self.processFile(headers, dataRows);
      };

      this.file.name = selectedFile.name;
      this.file.raw = selectedFile;
      reader.readAsArrayBuffer(selectedFile);
    },
    processFile(headers, dataRows) {
      var catalogColumns = detectCatalogColumns(headers);

      if (!catalogColumns.length) {
        this.showError(
          "No se encontraron columnas de puntuación transformada reconocidas."
        );
        return;
      }

      var groupingColumns = detectGroupingColumns(headers);

      if (!groupingColumns.length) {
        this.showError(
          "No se encontraron columnas de agrupación (con prefijo AGR-)."
        );
        return;
      }

      var result = calculateAveragesByForm(headers, dataRows);

      this.result = result;
      this.excludedCombinations = result.excluded;
      this.fileRowCount = dataRows.length;
      this.status = "calculado";
      this.errorMessage = "";
    },
    showError(message) {
      this.status = "error";
      this.errorMessage = message;
      this.$store.dispatch("notify", {
        message: message,
        type: "is-danger",
      });

      if (this.$refs.inputFile) {
        this.$refs.inputFile.value = null;
      }

      this.file = { name: "", raw: null };
    },
    cellValue(combination, column) {
      var value = combination.averages[column.code];

      return value === null || value === undefined ? null : round1(value);
    },
    cellText(combination, column) {
      var value = this.cellValue(combination, column);

      return value === null ? "—" : value;
    },
    cellStyle(combination, column) {
      var value = this.cellValue(combination, column);

      if (value === null) {
        return {};
      }

      var level = classifyValue(value, column.name, column.form);

      if (!level) {
        return {};
      }

      var colors = RANGO_FP_PALETTE[level];

      return {
        backgroundColor: colors.background,
        borderColor: colors.border,
        color: colors.text,
      };
    },
    downloadTemplate() {
      generateFrpTemplate(
        TRANSFORMED_SCORE_TEMPLATE_COLUMNS,
        "FRP SIETE-PLANTILLA DATOS APP.xlsx"
      );
    },
    downloadExcel() {
      if (this.status !== "calculado") {
        return;
      }

      var workbook = buildWorkbook(this.result);

      XLSX.writeFile(workbook, "Promedios por Forma.xlsx");
    },
  },
};
</script>

<style lang="scss" scoped>
.promedios-forma {
  padding: 20px 24px;
}

.pf-titulo {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.pf-descripcion {
  color: #7a7a7a;
  font-size: 13px;
  max-width: 100ch;
  margin: 6px 0 22px;
}

.pf-descripcion code {
  background: #f1eef7;
  color: #363636;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 11.5px;
}

.pf-upload-zone {
  border: 2px dashed #dbdbdb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fbfaf9;
  position: relative;
}

.pf-upload-zone--empty {
  flex-direction: column;
  text-align: center;
  padding: 48px 32px;
  gap: 14px;
}

.pf-upload-zone--done {
  padding: 26px;
  border-style: solid;
  border-color: #c3eab5;
  background: #f7fbf6;
}

.pf-upload-input {
  display: none;
}

.pf-upload-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #efe7f7;
  color: #522687;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pf-upload-zone--empty .pf-upload-icon {
  width: 56px;
  height: 56px;
}

.pf-upload-zone--empty .pf-upload-icon .material-icons {
  font-size: 26px;
}

.pf-upload-icon--done {
  background: #d9ead3;
  color: #2f6b3f;
}

.pf-upload-empty-text strong {
  display: block;
  font-size: 14.5px;
  margin-bottom: 6px;
}

.pf-upload-empty-text span {
  color: #7a7a7a;
  font-size: 12px;
  max-width: 46ch;
  display: inline-block;
}

.pf-upload-empty-text code {
  background: #f1eef7;
  color: #363636;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 11.5px;
}

.pf-upload-text strong {
  display: block;
  font-size: 13.5px;
  margin-bottom: 2px;
}

.pf-upload-text span {
  color: #7a7a7a;
  font-size: 12px;
}

.pf-upload-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.pf-upload-empty-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.pf-upload-error {
  color: #c0392b;
  font-size: 12.5px;
  margin-top: 8px;
}

.pf-btn {
  border: none;
  border-radius: 5px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pf-btn .material-icons {
  font-size: 16px;
}

.pf-btn-primary {
  background: #522687;
  color: #fff;
}

.pf-btn-ghost {
  background: transparent;
  color: #522687;
  border: 1px solid #efe7f7;
}

.pf-result-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 22px;
  margin: 22px 0 16px;
  font-size: 12.5px;
  color: #7a7a7a;
}

.pf-result-meta strong {
  color: #363636;
  font-variant-numeric: tabular-nums;
}

.pf-legend-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 18px;
}

.pf-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pf-toolbar-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  flex: 1 1 auto;
}

.pf-search {
  max-width: 380px;
  flex: 1 1 320px;
}

.pf-view-switch {
  display: inline-flex;
  align-items: center;
  background: #f3f3f3;
  border-radius: 999px;
  padding: 2px;
  gap: 1px;
  font-size: 11.5px;
  flex: 0 0 auto;
}

.pf-view-switch__opt {
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #9a94a3;
}

.pf-view-switch__opt--active {
  background: #ffffff;
  color: #522687;
  box-shadow: 0 1px 2px rgba(20, 10, 30, 0.1);
}

.pf-legend-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 11.5px;
  font-weight: 600;
}

.pf-table-wrap {
  overflow: auto;
  max-height: 480px;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
}

.pf-result-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  min-width: 920px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.pf-result-table th {
  background: #faf9fc;
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #7a7a7a;
  padding: 9px 12px;
  border-bottom: 1px solid #dbdbdb;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
}

.pf-result-table th.pf-col-group,
.pf-result-table th.pf-col-subgroup {
  color: #363636;
}

.pf-result-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #ececec;
  white-space: nowrap;
}

.pf-result-table td.pf-col-group {
  font-weight: 600;
  background: #ffffff;
}

.pf-result-table .pf-col-subgroup {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 1px 0 0 #dbdbdb;
}

.pf-result-table td.pf-col-subgroup {
  color: #7a7a7a;
  background: #ffffff;
  cursor: default;
}

.pf-col-n {
  text-align: center;
  color: #7a7a7a;
  font-variant-numeric: tabular-nums;
}

.pf-result-table th.pf-col-subgroup {
  background: #faf9fc;
  z-index: 3;
}

.pf-metric {
  text-align: right;
  font-weight: 700;
  border-radius: 4px;
  border: 1px solid transparent;
}

.pf-metric--blank {
  color: #c6c6c6;
  font-weight: 400;
  font-style: italic;
  text-align: center;
}

.pf-table-note {
  font-size: 11.5px;
  color: #7a7a7a;
  margin: 8px 2px 0;
}

.pf-excluidas-list {
  list-style: disc;
  padding-left: 20px;
  max-height: 320px;
  overflow-y: auto;
  font-size: 13px;
}

.pf-excluidas-list li {
  padding: 3px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/herramientas/promedios-forma/PromediosForma.vue